/**
 * Authenticated Fetch
 *
 * Wraps fetch with credentials and CSRF token for the BFF pattern.
 * - Always includes cookies (credentials: 'include')
 * - Adds X-CSRF-Token header for mutating requests (POST, PUT, DELETE, PATCH)
 * - On 401 response, redirects to login (session expired beyond recovery)
 */

let currentCsrfToken: string | null = null;

/**
 * Set the CSRF token (called by AuthProvider after /auth/me).
 */
export function setCsrfToken(token: string | null): void {
    currentCsrfToken = token;
}

/**
 * Get the current CSRF token.
 */
export function getCsrfToken(): string | null {
    return currentCsrfToken;
}

/**
 * Fetch wrapper that includes authentication credentials and CSRF protection.
 */
export async function authenticatedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const headers = new Headers(init?.headers);

    // Add CSRF token for state-changing methods
    const method = (init?.method || 'GET').toUpperCase();
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && currentCsrfToken) {
        headers.set('X-CSRF-Token', currentCsrfToken);
    }

    const response = await fetch(input, {
        ...init,
        headers,
        credentials: 'include',
    });

    // If 401, session is expired beyond recovery — redirect to login.
    // Exception: anonymous public viewer pages (scheme viewer + discover)
    // should never bounce to login. A spurious 401 from a child fetch on
    // those pages must surface as a normal error in the calling component,
    // not yank the visitor to OIDC.
    if (response.status === 401 && !isAnonymousPath(window.location.pathname)) {
        window.location.href = `/auth/login?return_url=${encodeURIComponent(window.location.pathname)}`;
    }

    return response;
}

function isAnonymousPath(pathname: string): boolean {
    if (pathname === '/') return true;
    if (pathname === '/schemeViewer' || pathname.startsWith('/schemeViewer/')) return true;
    return /^\/vocabularies\/[^/]+\/(schemeViewer|discover)\/?$/.test(pathname);
}
